import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Building2,
  DoorOpen,
  FileText,
  CreditCard,
  Users,
  MessageSquare,
  BarChart3,
  Receipt,
  Settings,
  LogOut,
  Crown,
  Wallet,
  X,
  Home,
  Bell,
  HelpCircle,
  HeadphonesIcon,
  Star,
  UserPlus,
  ScrollText,
  ShieldCheck,
  UserCog,
  Tag,
  TrendingUp,
  BookOpen,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials } from '../../lib/utils';
import { badgeAPI } from '../../lib/api';

const adminLinks = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/landlords', icon: Users, label: 'Landlords' },
  { to: '/admin/subscriptions', icon: Crown, label: 'Subscriptions' },
  { to: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { to: '/admin/support', icon: HeadphonesIcon, label: 'Support' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

const landlordLinks = [
  { to: '/landlord', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/landlord/properties', icon: Building2, label: 'Properties' },
  { to: '/landlord/units', icon: DoorOpen, label: 'Units' },
  { to: '/landlord/leases', icon: FileText, label: 'Leases' },
  { to: '/landlord/tenants', icon: Users, label: 'Tenants' },
  { to: '/landlord/managers', icon: UserCog, label: 'Managers' },
  { to: '/landlord/payments', icon: CreditCard, label: 'Payments' },
  { to: '/landlord/expenses', icon: Wallet, label: 'Expenses' },
  { to: '/landlord/bookkeeping', icon: BookOpen, label: 'Bookkeeping' },
  { to: '/landlord/messages', icon: MessageSquare, label: 'Messages' },
  { to: '/landlord/notifications', icon: Bell, label: 'Notifications' },
  { to: '/landlord/reports', icon: BarChart3, label: 'Reports & Analytics' },
  { to: '/landlord/ratings', icon: Star, label: 'My Ratings' },
  { to: '/landlord/support', icon: HeadphonesIcon, label: 'Support' },
  { to: '/landlord/settings', icon: Settings, label: 'Settings' },
];

const managerLinks = [
  { to: '/manager', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/manager/properties', icon: Building2, label: 'Properties' },
  { to: '/manager/units', icon: DoorOpen, label: 'Units' },
  { to: '/manager/leases', icon: FileText, label: 'Leases' },
  { to: '/manager/tenants', icon: Users, label: 'Tenants' },
  { to: '/manager/payments', icon: CreditCard, label: 'Payments' },
  { to: '/manager/expenses', icon: Wallet, label: 'Expenses' },
  { to: '/manager/messages', icon: MessageSquare, label: 'Messages' },
  { to: '/manager/notifications', icon: Bell, label: 'Notifications' },
  { to: '/manager/reports', icon: BarChart3, label: 'Reports & Analytics' },
  { to: '/manager/support', icon: HeadphonesIcon, label: 'Support' },
];

const tenantLinks = [
  { to: '/tenant', icon: Home, label: 'Dashboard', end: true },
  { to: '/tenant/lease', icon: FileText, label: 'My Lease' },
  { to: '/tenant/payments', icon: CreditCard, label: 'Payments' },
  { to: '/tenant/receipts', icon: Receipt, label: 'Receipts' },
  { to: '/tenant/documents', icon: FileText, label: 'Documents' },
  { to: '/tenant/messages', icon: MessageSquare, label: 'Messages' },
  { to: '/tenant/notifications', icon: Bell, label: 'Notifications' },
  { to: '/tenant/support', icon: HeadphonesIcon, label: 'Support' },
];

export function Sidebar({ isOpen, onClose, onHelpClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [badges, setBadges] = useState({});

  const loadBadges = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await badgeAPI.getCounts();
      setBadges(data || {});
    } catch {
      // non-critical
    }
  }, [user]);

  useEffect(() => {
    loadBadges();
    const interval = setInterval(loadBadges, 30000);
    const onRefresh = () => loadBadges();
    window.addEventListener('badge-refresh', onRefresh);
    return () => { clearInterval(interval); window.removeEventListener('badge-refresh', onRefresh); };
  }, [loadBadges, location.pathname]);

  const links = user?.role === 'admin' 
    ? adminLinks 
    : user?.role === 'landlord' 
    ? landlordLinks 
    : user?.role === 'property_manager'
    ? managerLinks
    : tenantLinks;

  const portalColor = user?.role === 'admin' 
    ? 'bg-indigo-500' 
    : user?.role === 'landlord' 
    ? 'bg-[#C85A42]' 
    : user?.role === 'property_manager'
    ? 'bg-[#C85A42]'
    : 'bg-[#6B8E6B]';

  const getRoleLabel = () => {
    if (user?.role === 'property_manager') return 'Property Manager';
    return user?.role;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 h-full w-64 bg-card border-r z-40 flex flex-col transition-transform duration-300',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="U-DORM" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-lg">U-DORM</h1>
              <p className="text-xs text-muted-foreground capitalize">{getRoleLabel()} Portal</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {links.map((link) => {
            const badgeCount = badges[link.to] || 0;
            return (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? `${portalColor} text-white`
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <link.icon className="w-5 h-5" />
              <span className="flex-1">{link.label}</span>
              {badgeCount > 0 && (
                <span
                  className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center"
                  data-testid={`nav-badge-${link.to.replace(/\//g, '-').replace(/^-/, '')}`}
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </NavLink>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t space-y-2">
          <NavLink
            to={`/${user?.role}/profile`}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 p-2 rounded-lg transition-colors hover:bg-muted',
                isActive && 'bg-muted'
              )
            }
            data-testid="profile-link"
          >
            <Avatar className="w-10 h-10">
              <AvatarImage src={user?.picture?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.picture}` : user?.picture} />
              <AvatarFallback className={cn('text-white text-sm', portalColor)}>
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </NavLink>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              onHelpClick?.();
              onClose?.();
            }}
            data-testid="help-tutorial-btn"
          >
            <HelpCircle className="w-4 h-4" />
            Help & Tutorial
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              const role = user?.role === 'property_manager' ? 'manager' : user?.role;
              navigate(`/${role}/faqs`);
              onClose?.();
            }}
            data-testid="faqs-btn"
          >
            <BookOpen className="w-4 h-4" />
            FAQs
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}
